import { useState } from "react"
import { router } from "@inertiajs/react"
import { StepShell } from "../components/StepShell"
import { DynamicList, TextFieldRow } from "../components/DynamicList"
import { Input } from "@/components/ui/input"
import { CurrencyUsdInput } from "@/components/ui/currency-usd-input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { GuideMeCta } from "@/components/planning/client-only/GuideMeCta"
import { parseOneYearPlanBullets } from "@/lib/guide-output"
import { moneyPropToString, normalizeMoneyCanonical } from "@/lib/money-input"

function defaultIssues(saved) {
  if (saved && saved.length > 0) return saved
  return Array.from({ length: 3 }, () => ({ text: "" }))
}

export function OneYearPlan({ data }) {
  const plan = data.annualPlan
  const [targetDate, setTargetDate] = useState(plan.targetDate || "")
  const [revenueTarget, setRevenueTarget] = useState(() => moneyPropToString(plan.revenueTarget))
  const [profitTarget, setProfitTarget] = useState(() => moneyPropToString(plan.profitTarget))
  const [measurables, setMeasurables] = useState(plan.measurables || "")
  const [issues, setIssues] = useState(() => defaultIssues(plan.issues))
  const [processing, setProcessing] = useState(false)

  const submit = () => {
    setProcessing(true)
    router.patch(
      "/app/plan/intake",
      {
        step: "one_year_plan",
        target_date: targetDate,
        revenue_target: normalizeMoneyCanonical(revenueTarget),
        profit_target: normalizeMoneyCanonical(profitTarget),
        measurables,
        issues,
        advance: "1",
      },
      {
        preserveScroll: true,
        onFinish: () => setProcessing(false),
      },
    )
  }

  // The one-year-plan guide uses labeled bullets (Revenue:, Profit:,
  // Measurables:, Issue:) so a single "Use this" click fills every
  // form field the AI surfaced — not just the issues list. Fields the
  // AI omitted are left alone so the user doesn't lose anything they
  // already typed.
  const insertGuidedPlan = (bullets) => {
    const parsed = parseOneYearPlanBullets(bullets)

    if (parsed.targetDate) setTargetDate(parsed.targetDate)
    if (parsed.revenueTarget !== null) setRevenueTarget(parsed.revenueTarget)
    if (parsed.profitTarget !== null) setProfitTarget(parsed.profitTarget)
    if (parsed.measurables !== null) setMeasurables(parsed.measurables)

    if (parsed.issues.length > 0) {
      const rows = parsed.issues.slice(0, 7).map((text) => ({ text }))
      while (rows.length < 1) rows.push({ text: "" })
      setIssues(rows)
    }
  }

  return (
    <StepShell
      title="Your one-year plan"
      subtitle="The concrete targets you're steering toward over the next 12 months."
      onNext={submit}
      processing={processing}
    >
      <GuideMeCta
        section="one_year_plan"
        onInsert={insertGuidedPlan}
        title="Stuck on the one-year plan?"
        subtitle="Talk it through with an AI coach — it knows your goals + wheel scores and will fill in revenue, profit, measurables, and the issues to solve."
      />
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="target_date">Target date</Label>
            <Input
              id="target_date"
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="revenue_target">Revenue target ($)</Label>
            <CurrencyUsdInput
              id="revenue_target"
              value={revenueTarget}
              onValueChange={setRevenueTarget}
              placeholder="$500,000"
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="profit_target">Profit target ($)</Label>
            <CurrencyUsdInput
              id="profit_target"
              value={profitTarget}
              onValueChange={setProfitTarget}
              placeholder="$100,000"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="measurables">Measurables</Label>
          <Textarea
            id="measurables"
            rows={3}
            value={measurables}
            onChange={(e) => setMeasurables(e.target.value)}
            placeholder="Other metrics that tell you you're on track…"
          />
        </div>

        <div>
          <Label>Top 3–7 issues to solve or address</Label>
          <p className="text-xs text-zinc-500 mb-2">
            The real blockers — what's in the way?
          </p>
          <DynamicList
            items={issues}
            onChange={setIssues}
            min={1}
            max={7}
            newItem={() => ({ text: "" })}
            addLabel="Add another issue"
            render={(item, update) => (
              <TextFieldRow
                value={item.text}
                onChange={(v) => update("text", v)}
                placeholder="e.g., Hiring an ops lead so I'm not in every detail"
              />
            )}
          />
        </div>
      </div>
    </StepShell>
  )
}
